import { type ReactNode, useEffect } from "react";
import { FormProvider, type UseFormReturn, useForm } from "react-hook-form";
import { useBuilderStore } from "../Hooks/useBuilderStore";

export interface BaseThemeFormData {
  selectedThemeId?: number;
  theme: {
    [key: string]: Record<string, string>;
  };
}

interface BaseThemeFormWrapperProps<T extends BaseThemeFormData> {
  children: ReactNode;
  defaultValues: T;
  footerContent?: () => ReactNode;
  onFormReady?: (methods: UseFormReturn<T>) => void;
  onReset?: (methods: UseFormReturn<T>) => void;
  onValueChange?: (methods: UseFormReturn<T>) => void;
  onWatch?: (methods: UseFormReturn<T>) => void;
  processVariableUpdate: (fieldValue: string, variableName: string) => void;
}

export function BaseThemeFormWrapper<T extends BaseThemeFormData>({
  children,
  defaultValues,
  footerContent,
  onFormReady,
  onReset,
  onValueChange,
  onWatch,
  processVariableUpdate,
}: BaseThemeFormWrapperProps<T>) {
  const currentCanvasAppearance = useBuilderStore(state => state.appearance);
  const currentCanvasThemeVariables = useBuilderStore(
    state => state.currentCanvasTheme.cssVariables,
  );

  const methods = useForm<T>({
    defaultValues: defaultValues as any,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { watch, getValues, reset } = methods;

  // Notify parent when form is ready
  useEffect(() => {
    if (onFormReady) {
      onFormReady(methods);
    }
  }, [onFormReady]);

  // Watch for form changes and update theme variables
  useEffect(() => {
    const subscription = watch((formData, { name }) => {
      if (!name || !formData.theme) return;

      const fieldValue = getValues(name);
      if (typeof fieldValue !== "string") return;

      processVariableUpdate(fieldValue, name);

      if (onWatch) {
        onWatch(methods);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, processVariableUpdate, getValues, onWatch]);

  // Reset form when appearance changes
  useEffect(() => {
    reset();
    if (onReset) {
      onReset(methods);
    }
  }, [reset, currentCanvasAppearance, onReset]);

  // Handle value changes
  useEffect(() => {
    if (onValueChange) {
      onValueChange(methods);
    }
  }, [currentCanvasThemeVariables, onValueChange]);

  const footerButtons = footerContent?.();

  return (
    <FormProvider {...methods}>
      <div className="flex h-[calc(100%-58px)] flex-col">
        <div className="h-full overflow-y-auto">{children}</div>
        {footerButtons && (
          <div className="bg-primary border-primary flex w-full justify-end gap-2 border-t px-6 py-3">
            {footerButtons}
          </div>
        )}
      </div>
    </FormProvider>
  );
}

// Helper hook for common theme form operations
export function useThemeFormHelpers() {
  const updateCurrentCanvasThemeVariables = useBuilderStore(
    state => state.updateCurrentCanvasThemeVariables,
  );
  const currentCanvasAppearance = useBuilderStore(state => state.appearance);
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);
  const toggleCurrentCanvasTheme = useBuilderStore(
    state => state.toggleCurrentCanvasTheme,
  );
  const googleFonts = useBuilderStore(state => state.googleFonts);

  return {
    updateCurrentCanvasThemeVariables,
    currentCanvasAppearance,
    currentCanvasTheme,
    toggleCurrentCanvasTheme,
    googleFonts,
  };
}
